import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getAboutYourBusinessURL,
  getAccAnnatureStatusURL,
  getBusinessDetailsURL,
  getDeleteSecondaryUserURL,
  getDirectorRosterURL,
  getDeleteTrustDetailsURL,
  getIdentificationURL,
  getOnboarStatusCheckUrl,
  getOnboardingRoleDataURL,
  getOnboardingURL,
  getOnboardOwnersDataURL,
  getOnboardOwnersListURL,
  getTrustAssociatesURL,
  getTrustDetailsURL,
  getUpdateAboutYourBusinessURL,
  getUpdateOnboardingRoleURL,
  getUpdateOnboardOwnersDataURL,
  getUserOnboardUrl,
  getVerifyIdentityDataURL,
  userManagementUsersURL
} from "../utils/constants/apiEndPoints";
import privateAxios from "../utils/axiosPrivate";
import { showToast } from "../utils/utils";
import { commonErrMsg } from "../locales/en";
import {
  SECONDARY_USER_TYPE,
  showToastType
} from "../utils/constants/appConstants";

const initialState = {
  stepsLoader: false,
  onBoardingStepsData: null,
  aboutYourBusinessData: null,
  onboardingRoleData: null,
  verifyIdentityLoader: false,
  verifyIdentityData: null,
  checkStatus: true,
  ownersDataLoader: false,
  ownersData: null,
  ownersListDataLoader: false,
  ownersListData: null,
  trusteeDataLoader: false,
  trusteeData: null,
  trusteeDataTrustType: null,
  trustAssociatesDataLoader: false,
  trustAssociatesData: null,
  businessDetailsDataLoader: false,
  businessDetailsData: null,
  directorRosterLoader: false,
  directorRosterData: [],
  accountUsersLoader: false,
  accountUsersData: [],
  applyIdStatusLoader: false,
  applyIdStatusData: null,
  annatureStatusLoader: false,
  annatureStatusData: null
};

export const getOnboardingStepsByUser = createAsyncThunk(
  "onboarding/getOnboardingStepsByUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getOnboardingURL(userId));
      return response?.data?.data ?? response?.data ?? response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getAboutYourBusiness = createAsyncThunk(
  "onboarding/getAboutYourBusiness",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getAboutYourBusinessURL(userId));
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getOnboardingRoleData = createAsyncThunk(
  "onboarding/getOnboardingRoleData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getOnboardingRoleDataURL(userId));
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getUpdateAboutYourBusiness = createAsyncThunk(
  "onboarding/getUpdateAboutYourBusiness",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(
        getUpdateAboutYourBusinessURL,
        payload
      );
      return {
        response,
        payload
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getUpdateOnboardingRole = createAsyncThunk(
  "onboarding/getUpdateOnboardingRole",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(
        getUpdateOnboardingRoleURL,
        payload
      );
      return {
        response,
        payload
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getVerifyIdentityData = createAsyncThunk(
  "onboarding/getVerifyIdentityData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getVerifyIdentityDataURL(userId));
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getUpdateVerifyIdentityData = createAsyncThunk(
  "onboarding/getUpdateVerifyIdentityData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(
        getVerifyIdentityDataURL(payload.userAccountId),
        payload
      );
      return {
        response,
        payload
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getCheckStatusData = createAsyncThunk(
  "onboarding/getCheckStatusData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getIdentificationURL);
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getOnboardingOwnerData = createAsyncThunk(
  "onboarding/getOnboardingOwnerData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getOnboardOwnersDataURL(userId));
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getUpdatePropertyDetailsData = createAsyncThunk(
  "onboarding/getUpdatePropertyDetailsData",
  async ({ apiPayload, owners }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(
        getUpdateOnboardOwnersDataURL,
        apiPayload
      );
      return {
        message: response.data.message,
        response,
        payload: apiPayload,
        owners
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getOnboardingOwnersListData = createAsyncThunk(
  "onboarding/getOnboardingOwnersListData",
  async (type, { rejectWithValue }) => {
    try {
      // A type narrows the list to the owners that section created; dispatching
      // without one returns every secondary user, as before.
      const response = await privateAxios.get(
        getOnboardOwnersListURL,
        type ? { params: { type } } : undefined
      );
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

// The people the Add Account Users section created, read back by their own type so
// this list and the Property Owners list never hold each other's people. They are
// secondary user accounts, the same as a property owner, rather than entries in the
// business_details map.
export const getAccountUsersListData = createAsyncThunk(
  "onboarding/getAccountUsersListData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getOnboardOwnersListURL, {
        params: { type: SECONDARY_USER_TYPE.ACCOUNT_USER }
      });
      return response?.data?.data ?? [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

// Removes one person the signed-in account holds. The list is refetched by the
// caller rather than filtered here, so the section shows what the server now holds.
export const getDeleteSecondaryUser = createAsyncThunk(
  "onboarding/getDeleteSecondaryUser",
  async (secondaryUserId, { rejectWithValue }) => {
    try {
      await privateAxios.delete(getDeleteSecondaryUserURL(secondaryUserId));
      return { secondaryUserId };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUpdateUserListData = createAsyncThunk(
  "onboarding/getUpdateUserListData",
  async ({ apiPayload }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(getUserOnboardUrl, apiPayload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDeleteUserListData = createAsyncThunk(
  "onboarding/getDeleteUserListData",
  async (ownerId, { rejectWithValue }) => {
    try {
      await privateAxios.delete(userManagementUsersURL(ownerId));
      return { ownerId };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getTrusteeData = createAsyncThunk(
  "onboarding/getTrusteeData",
  async ({ userId, trustType }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(
        getTrustDetailsURL(userId, trustType)
      );
      return response?.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getTrustAssociatesData = createAsyncThunk(
  "onboarding/getTrustAssociatesData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getTrustAssociatesURL(userId));
      return response?.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getDeleteTrusteeData = createAsyncThunk(
  "onboarding/getDeleteTrusteeData",
  async ({ associatesId }, { rejectWithValue }) => {
    try {
      await privateAxios.delete(getDeleteTrustDetailsURL(associatesId));
      return { associatesId };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getBusinessDetailsData = createAsyncThunk(
  "onboarding/getBusinessDetailsData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getBusinessDetailsURL(userId));
      return response?.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

// The account's directors, in the one shape user-management returns them in. Read
// this rather than picking directors out of businessDetailsData: several producers
// write that map in several record shapes across several lists, and every rule for
// reading it lives in user-management.
export const getDirectorRosterData = createAsyncThunk(
  "onboarding/getDirectorRosterData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getDirectorRosterURL(userId));
      return response?.data?.data ?? [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const fetchApplyIdStatus = createAsyncThunk(
  "onboarding/fetchApplyIdStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getOnboarStatusCheckUrl);
      return response?.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

export const getAnnatureCheckStatus = createAsyncThunk(
  "onboarding/getAnnatureCheckStatus",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.post(getAccAnnatureStatusURL, null, {
        params: { userAccountId: userId }
      });
      return response?.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? error
      );
    }
  }
);

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOnboardingStepsByUser.pending, (state) => {
        state.stepsLoader = true;
      })
      .addCase(getOnboardingStepsByUser.fulfilled, (state, action) => {
        state.stepsLoader = false;
        state.onBoardingStepsData = action.payload;
      })
      .addCase(getOnboardingStepsByUser.rejected, (state, action) => {
        state.stepsLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getAboutYourBusiness.pending, (state) => {
        state.stepsLoader = true;
      })
      .addCase(getAboutYourBusiness.fulfilled, (state, action) => {
        state.stepsLoader = false;
        state.aboutYourBusinessData =
          action.payload?.data?.data ?? action.payload;
      })
      .addCase(getAboutYourBusiness.rejected, (state, action) => {
        state.stepsLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getUpdateAboutYourBusiness.pending, (state) => {
        state.stepsLoader = true;
      })
      .addCase(getUpdateAboutYourBusiness.fulfilled, (state, action) => {
        state.stepsLoader = false;
        state.aboutYourBusinessData = {
          ...state.aboutYourBusinessData,
          ...action.payload.payload
        };
      })
      .addCase(getUpdateAboutYourBusiness.rejected, (state) => {
        state.stepsLoader = false;
      })

      .addCase(getOnboardingRoleData.fulfilled, (state, action) => {
        state.onboardingRoleData = action.payload?.data?.data ?? action.payload;
      })
      .addCase(getOnboardingRoleData.rejected, (state, action) => {
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      // The credit-consent flag comes from the saved record the server returns, never from the
      // request body. The role radio and the consent checkbox PUT to the same endpoint, so
      // echoing a request back would let a role write that finishes last leave the store
      // claiming a consent state the database does not hold - and the Verify Identity step is
      // rendered off that flag. A response without the saved record leaves the flag untouched.
      .addCase(getUpdateOnboardingRole.fulfilled, (state, action) => {
        const saved = action.payload?.response?.data?.data?.onboarding;
        const requested = action.payload?.payload ?? {};
        const next = { ...state.onboardingRoleData };

        if (requested.yourRole !== undefined) {
          next.yourRole = requested.yourRole;
        }
        if (saved) {
          next.yourRole = saved.yourRole ?? next.yourRole;
          next.isCreditConsentAccepted = Boolean(saved.isCreditConsentAccepted);
          next.consentDetails = saved.consentDetails ?? next.consentDetails;
        }

        state.onboardingRoleData = next;
      })

      .addCase(getVerifyIdentityData.pending, (state) => {
        state.verifyIdentityLoader = true;
      })
      .addCase(getVerifyIdentityData.fulfilled, (state, action) => {
        state.verifyIdentityData = action.payload?.data?.data ?? action.payload;
        state.verifyIdentityLoader = false;
      })
      .addCase(getVerifyIdentityData.rejected, (state, action) => {
        state.verifyIdentityLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getUpdateVerifyIdentityData.fulfilled, (state, action) => {
        state.verifyIdentityData = {
          ...state.verifyIdentityData,
          ...action.payload.payload
        };
      })

      .addCase(getCheckStatusData.rejected, (state, action) => {
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getOnboardingOwnerData.pending, (state) => {
        state.ownersDataLoader = true;
      })
      .addCase(getOnboardingOwnerData.fulfilled, (state, action) => {
        state.ownersData = action.payload?.data?.data;
        state.ownersDataLoader = false;
      })
      .addCase(getOnboardingOwnerData.rejected, (state, action) => {
        state.ownersDataLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getUpdatePropertyDetailsData.fulfilled, (state, action) => {
        const payload = action.payload.payload;
        state.ownersData = {
          ...state.ownersData,

          propertyDetails: {
            ...(state.ownersData?.propertyDetails || {}),
            address: payload.propertyDetails?.address ?? ""
          },

          ownershipType: payload.propertyOwnership,
          trustEntityName: payload.trustDetails?.entityName ?? "",
          companyName: payload.directorDetails?.companyName ?? "",
          noMortgage: payload.noMortgage
        };
      })

      .addCase(getOnboardingOwnersListData.pending, (state) => {
        state.ownersListDataLoader = true;
      })
      .addCase(getOnboardingOwnersListData.fulfilled, (state, action) => {
        state.ownersListData = action.payload;
        state.ownersListDataLoader = false;
      })
      .addCase(getOnboardingOwnersListData.rejected, (state, action) => {
        state.ownersListDataLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getDeleteUserListData.fulfilled, (state, action) => {
        if (!Array.isArray(state.ownersListData)) return;

        state.ownersListData = state.ownersListData.filter(
          (owner) => owner.id !== action.payload.ownerId
        );
      })

      .addCase(getTrusteeData.pending, (state, action) => {
        state.trusteeDataLoader = true;
        if (state.trusteeDataTrustType !== action.meta.arg?.trustType) {
          state.trusteeData = null;
        }
      })
      .addCase(getTrusteeData.fulfilled, (state, action) => {
        state.trusteeDataLoader = false;
        state.trusteeData = action.payload;
        state.trusteeDataTrustType = action.meta.arg?.trustType;
      })
      .addCase(getTrusteeData.rejected, (state, action) => {
        state.trusteeDataLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getTrustAssociatesData.pending, (state) => {
        state.trustAssociatesDataLoader = true;
      })
      .addCase(getTrustAssociatesData.fulfilled, (state, action) => {
        state.trustAssociatesDataLoader = false;
        state.trustAssociatesData = action.payload;
      })
      .addCase(getTrustAssociatesData.rejected, (state, action) => {
        state.trustAssociatesDataLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getDeleteTrusteeData.fulfilled, (state, action) => {
        if (!Array.isArray(state.trusteeData?.corporateTrustAssociates)) return;

        state.trusteeData = {
          ...state.trusteeData,
          corporateTrustAssociates:
            state.trusteeData.corporateTrustAssociates.filter(
              (associate) => associate.id !== action.payload.associatesId
            )
        };
      })
      .addCase(getDeleteTrusteeData.rejected, (state, action) => {
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getBusinessDetailsData.pending, (state) => {
        state.businessDetailsDataLoader = true;
      })
      .addCase(getBusinessDetailsData.fulfilled, (state, action) => {
        state.businessDetailsDataLoader = false;
        state.businessDetailsData = action.payload;
      })
      .addCase(getBusinessDetailsData.rejected, (state, action) => {
        state.businessDetailsDataLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getAccountUsersListData.pending, (state) => {
        state.accountUsersLoader = true;
      })
      .addCase(getAccountUsersListData.fulfilled, (state, action) => {
        state.accountUsersLoader = false;
        state.accountUsersData = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(getAccountUsersListData.rejected, (state, action) => {
        state.accountUsersLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getDirectorRosterData.pending, (state) => {
        state.directorRosterLoader = true;
      })
      .addCase(getDirectorRosterData.fulfilled, (state, action) => {
        state.directorRosterLoader = false;
        state.directorRosterData = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(getDirectorRosterData.rejected, (state) => {
        // No toast. The roster only names the people a card is waiting on, so a
        // failed read costs the name and nothing else - the card still says what
        // is outstanding. A toast here would report a problem the reader has no
        // action for.
        state.directorRosterLoader = false;
        state.directorRosterData = [];
      })

      .addCase(fetchApplyIdStatus.pending, (state) => {
        state.applyIdStatusLoader = true;
      })
      .addCase(fetchApplyIdStatus.fulfilled, (state, action) => {
        state.applyIdStatusLoader = false;
        state.applyIdStatusData = action.payload;
      })
      .addCase(fetchApplyIdStatus.rejected, (state, action) => {
        state.applyIdStatusLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      })

      .addCase(getAnnatureCheckStatus.pending, (state) => {
        state.annatureStatusLoader = true;
      })
      .addCase(getAnnatureCheckStatus.fulfilled, (state, action) => {
        state.annatureStatusLoader = false;
        state.annatureStatusData = action.payload;
      })
      .addCase(getAnnatureCheckStatus.rejected, (state, action) => {
        state.annatureStatusLoader = false;
        showToast(action.payload || commonErrMsg.item2, showToastType.error);
      });
  }
});

export default onboardingSlice.reducer;
